import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  HelpCircle,
  Link2,
  RefreshCw,
  Search,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { formatCurrency, formatDate } from '@/lib/formatters';
import {
  useAmazonBusinessOrder,
  useAmazonBusinessOrders,
  useImportAmazonBusinessCsv,
  useLinkAmazonBusinessOrder,
  useRerunAmazonBusinessMatch,
  useUpdateAmazonBusinessLineQbAccount,
} from '@/features/amazonBusiness/hooks';
import { useChartOfAccounts } from '@/features/financials/hooks';
import type {
  AmazonBusinessMatchStatus,
  Tables,
} from '@/types/database';

// Sentinel used by the per-line QB Category select to mean "no category
// assigned yet". Radix Select can't store an empty string in its value,
// so we round-trip through this constant and translate to NULL at the
// DB boundary (matches the TransactionsPage convention).
const QB_NONE = '__none__';

const STATUS_FILTERS: Array<{ value: AmazonBusinessMatchStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All orders' },
  { value: 'auto_matched', label: 'Auto-matched' },
  { value: 'manual_matched', label: 'Manually linked' },
  { value: 'review_required', label: 'Review required' },
  { value: 'no_charge_yet', label: 'Awaiting charge' },
  { value: 'unmatched', label: 'Unmatched' },
];

/**
 * Amazon Business orders ledger. Replaces what QBO's Amazon Business
 * integration used to surface, with Atlas as the source of truth:
 *
 *   - Top: import + matcher controls (mirrors the IntegrationsPage card
 *     so the operator can re-upload without context-switching).
 *   - Middle: filterable orders table from
 *     v_amazon_business_orders_with_match.
 *   - Right drawer: line-item table with per-line QB Category select,
 *     match disambiguator when the matcher returned multiple
 *     candidates, and a peek at the matched Plaid transaction (with a
 *     deep link into /financials/transactions/:id).
 */
export function AmazonBusinessPage() {
  const [statusFilter, setStatusFilter] = useState<AmazonBusinessMatchStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);

  const orders = useAmazonBusinessOrders({ status: statusFilter });
  const importCsv = useImportAmazonBusinessCsv();
  const rerunMatch = useRerunAmazonBusinessMatch();

  const rows = orders.data ?? [];
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (r) =>
        r.amazon_order_id.toLowerCase().includes(needle) ||
        (r.buyer_name ?? '').toLowerCase().includes(needle) ||
        (r.buyer_email ?? '').toLowerCase().includes(needle) ||
        (r.matched_counterparty ?? '').toLowerCase().includes(needle),
    );
  }, [rows, search]);

  // Summary chips at the top of the page so the operator sees the
  // backlog without scrolling.
  const summary = useMemo(() => {
    const init = {
      total: rows.length,
      review_required: 0,
      no_charge_yet: 0,
      unmatched: 0,
      uncategorized_lines: 0,
    };
    for (const r of rows) {
      if (r.match_status === 'review_required') init.review_required++;
      if (r.match_status === 'no_charge_yet') init.no_charge_yet++;
      if (r.match_status === 'unmatched') init.unmatched++;
      init.uncategorized_lines += Math.max(0, r.line_count - r.categorized_count);
    }
    return init;
  }, [rows]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const result = await importCsv.mutateAsync(file);
      const m = result.match;
      const matchBits = m
        ? ` · ${m.auto_matched} auto-matched / ${m.review_required} need review`
        : '';
      toast.success(
        `Imported ${result.imported_orders} order${result.imported_orders === 1 ? '' : 's'}${matchBits}.`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Import failed');
    }
  };

  const handleRerunMatch = async () => {
    try {
      const res = await rerunMatch.mutateAsync();
      const s = res.summary;
      toast.success(
        `Matcher: ${s.auto_matched} matched, ${s.review_required} need review, ` +
          `${s.no_charge_yet} awaiting charge.`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Matcher failed');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Amazon Business"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleUpload}
              />
              <Button asChild>
                <span>
                  <Upload className="h-4 w-4" />
                  {importCsv.isPending ? 'Importing…' : 'Upload CSV'}
                </span>
              </Button>
            </label>
            <Button
              variant="outline"
              onClick={handleRerunMatch}
              disabled={rerunMatch.isPending}
            >
              <RefreshCw className={`h-4 w-4 ${rerunMatch.isPending ? 'animate-spin' : ''}`} />
              {rerunMatch.isPending ? 'Matching…' : 'Re-run matcher'}
            </Button>
          </div>
        }
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <SummaryTile label="Total orders" value={summary.total} />
        <SummaryTile
          label="Review required"
          value={summary.review_required}
          tone={summary.review_required > 0 ? 'warning' : 'muted'}
        />
        <SummaryTile
          label="Awaiting charge"
          value={summary.no_charge_yet}
          tone={summary.no_charge_yet > 0 ? 'info' : 'muted'}
        />
        <SummaryTile
          label="Unmatched"
          value={summary.unmatched}
          tone={summary.unmatched > 0 ? 'warning' : 'muted'}
        />
        <SummaryTile
          label="Uncategorised lines"
          value={summary.uncategorized_lines}
          tone={summary.uncategorized_lines > 0 ? 'warning' : 'muted'}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <CardTitle>Orders</CardTitle>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="h-9 pl-8 text-sm"
                  placeholder="Search order #, buyer…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={(v) =>
                  setStatusFilter(v as AmazonBusinessMatchStatus | 'all')
                }
              >
                <SelectTrigger className="h-9 w-[180px] text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTERS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {orders.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="No Amazon Business orders yet"
            />
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Lines</TableHead>
                    <TableHead>Match</TableHead>
                    <TableHead>Charge</TableHead>
                    <TableHead>QBO</TableHead>
                    <TableHead>Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => setOpenOrderId(row.id)}
                    >
                      <TableCell className="font-mono text-xs">
                        {row.amazon_order_id}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(row.order_date)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(row.order_total_cents)}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {row.categorized_count}/{row.line_count} categorised
                        </span>
                      </TableCell>
                      <TableCell>
                        <MatchStatusBadge status={row.match_status} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.matched_transaction_id ? (
                          <Link
                            to={`/financials/transactions/${row.matched_transaction_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 underline underline-offset-2"
                          >
                            {row.matched_posted_at
                              ? formatDate(row.matched_posted_at)
                              : 'View'}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <QboBadge status={row.matched_qb_post_status} />
                      </TableCell>
                      <TableCell>
                        <Badge variant="muted" className="font-mono text-[10px] uppercase">
                          {row.source}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <OrderDetailSheet
        orderId={openOrderId}
        onOpenChange={(open) => {
          if (!open) setOpenOrderId(null);
        }}
      />
    </div>
  );
}

function SummaryTile({
  label,
  value,
  tone = 'muted',
}: {
  label: string;
  value: number;
  tone?: 'muted' | 'warning' | 'info';
}) {
  const toneClass =
    tone === 'warning'
      ? 'border-fresh-salmon/30 bg-fresh-salmon/5'
      : tone === 'info'
        ? 'border-secondary/40 bg-secondary/10'
        : 'border-border bg-card/50';
  return (
    <div className={`rounded-lg border ${toneClass} px-3 py-2`}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-xl font-medium tracking-headline text-foreground">{value}</p>
    </div>
  );
}

function MatchStatusBadge({ status }: { status: AmazonBusinessMatchStatus }) {
  if (status === 'auto_matched')
    return (
      <Badge variant="success" className="gap-1">
        <CheckCircle2 className="h-3 w-3" /> Auto
      </Badge>
    );
  if (status === 'manual_matched')
    return (
      <Badge variant="success" className="gap-1">
        <Link2 className="h-3 w-3" /> Linked
      </Badge>
    );
  if (status === 'review_required')
    return (
      <Badge variant="warning" className="gap-1">
        <AlertTriangle className="h-3 w-3" /> Review
      </Badge>
    );
  if (status === 'no_charge_yet')
    return (
      <Badge variant="info" className="gap-1">
        <Clock className="h-3 w-3" /> Awaiting
      </Badge>
    );
  return (
    <Badge variant="muted" className="gap-1">
      <HelpCircle className="h-3 w-3" /> Unmatched
    </Badge>
  );
}

function QboBadge({ status }: { status: string | null }) {
  if (!status || status === 'unassigned')
    return <Badge variant="muted">—</Badge>;
  if (status === 'posted') return <Badge variant="success">Posted</Badge>;
  if (status === 'posting') return <Badge variant="info">Posting</Badge>;
  if (status === 'failed') return <Badge variant="warning">Failed</Badge>;
  if (status === 'skipped') return <Badge variant="muted">Skipped</Badge>;
  if (status === 'pending' || status === 'awaiting_approval')
    return <Badge variant="info">Pending</Badge>;
  return <Badge variant="muted">{status}</Badge>;
}

/**
 * Side drawer with the per-line breakdown for one order. Per-line QB
 * Category select is the daily-driver workflow; everything else
 * (matcher disambiguator, charge link, raw totals) sits below.
 */
function OrderDetailSheet({
  orderId,
  onOpenChange,
}: {
  orderId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const detail = useAmazonBusinessOrder(orderId);
  const { data: coa } = useChartOfAccounts();
  const updateLine = useUpdateAmazonBusinessLineQbAccount();
  const linkOrder = useLinkAmazonBusinessOrder();

  const expenseAccounts = useMemo(() => {
    const accts = (coa ?? []).filter((a) => a.account_type === 'expense');
    return accts.sort((a, b) => (a.code ?? a.name).localeCompare(b.code ?? b.name));
  }, [coa]);

  const handleSetLineAccount = async (lineId: string, value: string) => {
    try {
      await updateLine.mutateAsync({
        line_id: lineId,
        qb_account_id: value === QB_NONE ? null : value,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const handleLink = async (orderIdToLink: string, transactionId: string) => {
    try {
      await linkOrder.mutateAsync({
        order_id: orderIdToLink,
        transaction_id: transactionId,
      });
      toast.success('Linked to the chosen charge. The matcher will respect this.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Link failed');
    }
  };

  const header = detail.data?.header;
  const lines = detail.data?.lines ?? [];

  return (
    <Sheet open={!!orderId} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle>
            Amazon Business order{' '}
            {header && (
              <span className="font-mono text-base">{header.amazon_order_id}</span>
            )}
          </SheetTitle>
          <SheetDescription>
            {header
              ? `Placed ${formatDate(header.order_date)} · ${
                  formatCurrency(header.order_total_cents)
                } · ${header.line_count} line${header.line_count === 1 ? '' : 's'}`
              : ' '}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 overflow-y-auto px-6 py-4">
          {detail.isLoading || !header ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <>
              {/* Match status banner */}
              {header.match_status === 'auto_matched' || header.match_status === 'manual_matched' ? (
                <div className="rounded-md border border-fresh-mint/40 bg-fresh-mint/5 px-3 py-2 text-xs">
                  <strong>Charge linked.</strong>{' '}
                  {header.matched_transaction_id && (
                    <Link
                      to={`/financials/transactions/${header.matched_transaction_id}`}
                      className="underline underline-offset-2"
                    >
                      View Plaid transaction
                    </Link>
                  )}
                </div>
              ) : header.match_status === 'review_required' ? (
                <ReviewDisambiguator
                  orderId={header.id}
                  candidates={(header.match_candidates as unknown) as MatchCandidate[]}
                  onPick={handleLink}
                  busy={linkOrder.isPending}
                />
              ) : header.match_status === 'no_charge_yet' ? (
                <div className="rounded-md border border-secondary/40 bg-secondary/10 px-3 py-2 text-xs">
                  <strong>Awaiting charge.</strong> No Plaid match for{' '}
                  {formatCurrency(header.order_total_cents)} near{' '}
                  {formatDate(header.order_date)}.
                </div>
              ) : (
                <div className="rounded-md border border-border bg-card/50 px-3 py-2 text-xs text-muted-foreground">
                  No match yet.
                </div>
              )}

              {/* Lines */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Line items
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    {header.categorized_count}/{header.line_count} have a QB
                    Category
                  </span>
                </div>
                <div className="overflow-x-auto rounded-md border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit</TableHead>
                        <TableHead className="text-right">Line total</TableHead>
                        <TableHead>QB Category</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell>
                            <div className="space-y-0.5">
                              <p className="line-clamp-2 text-sm">{line.title}</p>
                              {(line.asin || line.sku) && (
                                <p className="font-mono text-[10px] uppercase text-muted-foreground">
                                  {line.asin && <span>ASIN {line.asin}</span>}
                                  {line.asin && line.sku && ' · '}
                                  {line.sku && <span>SKU {line.sku}</span>}
                                  {line.items && (
                                    <>
                                      {' · '}
                                      <Link
                                        to={`/catalog/items/${line.items.id}`}
                                        className="underline underline-offset-2"
                                      >
                                        Item: {line.items.name}
                                      </Link>
                                    </>
                                  )}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{line.qty}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">
                            {formatCurrency(line.unit_price_cents)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatCurrency(line.line_total_cents)}
                          </TableCell>
                          <TableCell className="min-w-[220px]">
                            <Select
                              value={line.qb_account_id ?? QB_NONE}
                              onValueChange={(v) => handleSetLineAccount(line.id, v)}
                              disabled={updateLine.isPending}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Pick a category" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={QB_NONE}>
                                  <span className="text-muted-foreground">— None —</span>
                                </SelectItem>
                                {expenseAccounts.map((a) => (
                                  <SelectItem key={a.id} value={a.id}>
                                    {formatQbLabel(a)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Totals breakdown */}
              <div className="grid grid-cols-3 gap-3 text-sm">
                <Stat label="Subtotal" value={header.order_total_cents - header.shipping_cents - header.tax_cents} />
                <Stat label="Shipping" value={header.shipping_cents} />
                <Stat label="Tax" value={header.tax_cents} />
              </div>
            </>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

interface MatchCandidate {
  transaction_id: string;
  posted_at: string;
  amount_cents: number;
  counterparty: string | null;
  description: string | null;
}

function ReviewDisambiguator({
  orderId,
  candidates,
  onPick,
  busy,
}: {
  orderId: string;
  candidates: MatchCandidate[];
  onPick: (orderId: string, transactionId: string) => void | Promise<void>;
  busy: boolean;
}) {
  return (
    <div className="space-y-2 rounded-md border border-fresh-salmon/40 bg-fresh-salmon/5 px-3 py-3">
      <p className="text-xs">
        <strong>Multiple matching charges.</strong> Pick the Plaid transaction
        that paid for this order; the matcher won't override your choice.
      </p>
      <div className="space-y-1.5">
        {candidates.map((c) => (
          <div
            key={c.transaction_id}
            className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2 text-xs"
          >
            <div className="space-y-0.5">
              <p className="font-medium">
                {c.counterparty ?? c.description ?? 'Unknown counterparty'}
              </p>
              <p className="text-muted-foreground">
                {formatDate(c.posted_at)} · {formatCurrency(c.amount_cents)}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void onPick(orderId, c.transaction_id)}
            >
              <Link2 className="h-3.5 w-3.5" /> Use this charge
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border bg-card/40 px-3 py-2">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="tabular-nums">{formatCurrency(value)}</p>
    </div>
  );
}

function formatQbLabel(a: Pick<Tables<'chart_of_accounts'>, 'code' | 'name'>): string {
  return a.code ? `${a.code} · ${a.name}` : a.name;
}
